import SaveButton from "@components/button/SaveButton.component";
import PipelineNodeSubnav from "@components/pipelineNodes/PipelineNodeSubnav.component";
import PageStructure, { PageContent, PageContentHeader, PageContentInner, PageContentNoSidebar, PageSidebar, Pane, PaneContent, PaneContentWithSubnav } from "@pages/PageStructure.component";
import { getNodeTypeConfig } from "@services/modeling.service";
import { usePipelineNode } from "@stores/data.store";
import { forwardRef, ReactNode, useMemo, useRef, useCallback, useState } from "react";
import { Link, Outlet, useLocation, useParams } from "react-router-dom"
import { ButtonGroup, Form, Dropdown as BSDropdown, Button, Offcanvas, OffcanvasBody } from "react-bootstrap";
import PipelineNodeHelper from "@components/pipelineNodes/PipelineNodeHelper.component";

interface ToggleProps {
    children: ReactNode;
    onClick: (e: React.MouseEvent) => void;
    className?: string;
}
const CustomToggle = forwardRef((props: ToggleProps, ref) => (
    <button
        className={props.className}
        // @ts-ignore
        ref={ref}
        onClick={(e) => {
            e.preventDefault();
            props.onClick(e);
        }}
    >
      {props.children}
    </button>
  ));

const PipelineNodeWrapper = () => {
    const { pipelineNodeId } = useParams();

    const pipelineNode = usePipelineNode(pipelineNodeId)

    const location = useLocation();
    const activePage = useMemo(() => {
        let path = location.pathname;

        if (path.endsWith('/')) {
            path = path.substring(0, path.length - 1);
        }
        
        if (path.endsWith('/config')) {
            return 'config';
        } else if (path.endsWith('/files')) {
            return 'files';
        } else if (path.indexOf('/relationships') >= 0) {
            return 'relationships';
        } else if (path.endsWith('/insights')) {
            return 'insights';
        } else if (path.endsWith('/filters')) {
            return 'filters';
        } else if (path.indexOf('/visualizations') >= 0) {
            return 'visualizations';
        } else if (path.endsWith('/data')) {
            return 'data';
        }
        return '';
    }, [location]);

    const nodeConfig = useMemo(() => {
        if (!pipelineNode.data) {
            return undefined;
        }
        return getNodeTypeConfig(pipelineNode.data);
    }, [pipelineNode.dataUpdatedAt]);

    // Ref to hold the save function from the child component
    const saveRef = useRef<(() => Promise<void>) | null>(null);
    
    // State to track if the page is dirty (has unsaved changes)
    const [pageDirty, setPageDirty] = useState(false);

    // Callback to trigger save from parent
    const handleSave = useCallback(async () => {
        if (saveRef.current) {
            await saveRef.current();
        }
    }, []);

    // Context value to pass to child via Outlet
    const outletContext = useMemo(() => ({
        registerSave: (saveFn: () => Promise<void>) => {
            saveRef.current = saveFn;
        },
        unregisterSave: () => {
            saveRef.current = null;
        },
        setPageDirty: (dirty: boolean) => {
            setPageDirty(dirty);
        }
    }), []);

    return <PageStructure
        pageTitle={pipelineNode.data?.name || ''}
        breadcrumbs={[]}
    >
        <PageContent>
        <PipelineNodeSubnav 
            pipelineNodeId={pipelineNodeId as string} 
            enableSearch={activePage === 'data'}
            showSaveButton={activePage === 'config'}
            onSave={handleSave}
            saveDisabled={!pageDirty}
        />

            <PageContentInner hasHeader noScroll>
                <div style={{height: '100%', display: 'flex', width: '100%'}}>
                    <div style={{width: '75%', height: '100%'}}>
                        <Outlet context={outletContext}/>
                    </div>
                    <div className="bg-light" style={{width: '25%', height: '100%', borderLeft: 'solid 1px var(--ct-border-color)', overflowY: 'auto'}}>
                        <Pane>
                            <PaneContent>
                                <PipelineNodeHelper pipelineNodeId={pipelineNodeId as string}/>
                            </PaneContent>
                        </Pane>
                    </div>
                </div>
            </PageContentInner>
        </PageContent>
    </PageStructure>
}

export default PipelineNodeWrapper;